import React from 'react'

import { inject, observer } from 'mobx-react'

import EquipmentEditPanel from '~/client/src/desktop/components/EquipmentResource/EquipmentEditPanel'
import { Resource } from '~/client/src/desktop/components/Resource'
import { ResourceExportDialog } from '~/client/src/desktop/components/ResourceExportDialog/ResourceExportDialog'
import { ResourcesImportDialog } from '~/client/src/desktop/components/ResourcesImportDialog/ResourcesImportDialog'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { EquipmentProvider } from '~/client/src/desktop/views/Equipment/EquipmentProvider'
import EquipmentViewWrapper from '~/client/src/desktop/views/Equipment/EquipmentViewWrapper'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { useEquipmentResourceStore } from '~/client/src/shared/hooks/useEquipmentResourceStore'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'

function EquipmentObjects({
  state,
  fileUploadingStore,
}: {
  state?: DesktopInitialState
  fileUploadingStore?: FileUploadingStore
}) {
  const projectId = state?.activeProject?.id || ''
  const userId = state?.user?.id || ''
  const resourceTypeId = ResourceTypeId.OFFLOADING_EQUIPMENT

  const [isExportDialogOpen, setIsExportDialogOpen] = React.useState(false)
  const [isImportDialogOpen, setIsImportDialogOpen] = React.useState(false)
  const { schema } = useEquipmentResourceStore(projectId || undefined)
  const { properties, loading } = schema

  const handleImport = () => {
    setIsImportDialogOpen(true)
  }

  const handleCloseImportDialog = () => {
    setIsImportDialogOpen(false)
  }

  const handleExport = () => {
    setIsExportDialogOpen(true)
  }

  const handleCloseExportDialog = () => {
    setIsExportDialogOpen(false)
  }

  if (!projectId || !userId || !state) {
    return null
  }

  return (
    <EquipmentViewWrapper className="tw:h-full tw:flex tw:flex-col">
      <EquipmentProvider projectId={projectId} userId={userId} state={state}>
        <div className="multi-grid-container tw:relative tw:overflow-visible tw:flex tw:flex-col tw:h-full tw:min-h-0">
          <div className="tw:grow-0">
            <Resource.Header>
              <Resource.Search />
              <Resource.Separator />
              <Resource.Filters />
              <Resource.Actions>
                <Resource.DisplayOptions />
              </Resource.Actions>
            </Resource.Header>
          </div>
          <div className="tw:flex-1 tw:min-h-0">
            <Resource.Content>
              <Resource.ActionBar>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Resource.CollapseToggle />
                  <Resource.RecordCount
                    label={c => Localization.translator.xEquipment(c)}
                  />
                  <Resource.DeleteButton
                    confirmationLabel={c =>
                      Localization.translator.confirmDeletionOfEquipment(c)
                    }
                  />
                  <Resource.BulkEditButton />
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Resource.ImportButton onClick={handleImport} />
                  <Resource.ExportButton onClick={handleExport} />
                  <Resource.CreateButton />
                </div>
              </Resource.ActionBar>
              <Resource.Table />
            </Resource.Content>
          </div>
        </div>
        <EquipmentEditPanel />
        <Resource.BulkEditPanel />
      </EquipmentProvider>
      <ResourceExportDialog
        isOpen={isExportDialogOpen}
        onClose={handleCloseExportDialog}
        projectId={projectId}
        resourceTypeId={resourceTypeId}
        properties={properties}
        loading={loading}
        propertiesLabel={Localization.translator.equipmentProperties}
      />
      {isImportDialogOpen && (
        <ResourcesImportDialog
          isOpen={isImportDialogOpen}
          onClose={handleCloseImportDialog}
          projectId={projectId}
          resourceTypeId={resourceTypeId}
          fileUploadingStore={fileUploadingStore}
          resourceSchema={schema}
        />
      )}
    </EquipmentViewWrapper>
  )
}

export default inject('state', 'fileUploadingStore')(observer(EquipmentObjects))
